// Report tab — loads /reports/data, shows executive summary + findings detail.

import { Card } from "@/components/ui/Card";
import { Loading } from "@/components/ui/Loading";
import { SEVERITY_META, SeverityBadge } from "@/components/ui/SeverityBadge";
import { useEngagementReport } from "@/hooks/useEngagementDetail";
import { useDict } from "@/i18n/LocaleProvider";

export function ReportTab({ engagementId }: { engagementId: string }) {
  const t = useDict().app.report;
  const { data, isLoading } = useEngagementReport(engagementId, true);

  if (isLoading || !data) return <Loading text={t.generating} />;

  const m = data.metadata;
  const s = data.executive_summary;

  const riskColor =
    s.risk_rating === "CRITICAL"
      ? "rgb(var(--color-danger))"
      : s.risk_rating?.includes("HIGH")
        ? "rgb(var(--color-warning))"
        : "rgb(var(--color-content))";

  return (
    <div className="flex flex-col gap-6">
      {/* Report header — title prominent, meta facts on their own lines.
          On mobile the legacy "Client: X · Generated: Y" inline string
          wraps awkwardly mid-separator; key/value rows stay legible. */}
      <Card>
        <div className="font-display text-[22px] sm:text-[26px] font-bold text-content leading-[1.2] break-words mb-3">
          {m.title}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 font-mono text-[11px]">
          <div>
            <span className="text-muted uppercase tracking-[0.05em]">{t.client}</span>
            <span className="text-content ml-2">{m.client}</span>
          </div>
          <div>
            <span className="text-muted uppercase tracking-[0.05em]">{t.generated}</span>
            <span className="text-content ml-2">
              {new Date(m.generated_at).toLocaleString()}
            </span>
          </div>
        </div>
      </Card>

      {/* Executive summary — document-style key/value rows with dividers
          between them so each metric reads as a distinct line item rather
          than a free-floating number. The severity breakdown sits below
          a divider as its own labeled block. */}
      <Card>
        <div className="font-mono text-[11px] text-accent uppercase tracking-[0.08em] mb-4">
          {t.executiveSummary}
        </div>
        <dl className="font-mono text-[12px]">
          <SummaryRow label={t.riskRating}>
            <span style={{ color: riskColor }} className="font-bold uppercase tracking-[0.05em]">
              {s.risk_rating ?? "—"}
            </span>
          </SummaryRow>
          <SummaryRow label={t.totalFindings}>
            <span className="text-content">{s.total_findings ?? 0}</span>
          </SummaryRow>
          <SummaryRow label={t.evidenceCollected}>
            <span className="text-content">{s.evidence_collected ?? 0}</span>
          </SummaryRow>
          <SummaryRow label={t.assetsTested}>
            <span className="text-content">{s.assets_tested ?? 0}</span>
          </SummaryRow>
        </dl>
        {/* Severity breakdown — visually separated from the metric rows
            above with a divider + label so it reads as its own block. */}
        <div className="mt-5 pt-4 border-t border-stroke">
          <div className="font-mono text-[10px] text-muted uppercase tracking-[0.08em] mb-3">
            {t.severityBreakdown}
          </div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(s.severity_breakdown ?? {}).map(([k, v]) => {
              const meta = SEVERITY_META[k] ?? SEVERITY_META.informational;
              const color = v > 0 ? meta.c : "rgb(var(--color-dim))";
              return (
                <div
                  key={k}
                  className="flex items-baseline gap-2 px-3 py-2 rounded font-mono"
                  style={{
                    background: v > 0 ? `${color}11` : "rgb(var(--color-bg2))",
                    border: `1px solid ${color}55`,
                  }}
                >
                  <span className="text-[16px] font-bold font-display" style={{ color }}>
                    {v}
                  </span>
                  <span className="text-[9px] uppercase tracking-[0.05em] text-muted">
                    {k}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </Card>

      <Card>
        <div className="font-mono text-[11px] text-accent uppercase tracking-[0.08em] mb-4">
          {t.findingsDetail}
        </div>
        {(data.findings ?? []).map((f) => (
          <div
            key={f.id}
            className="mb-4 pb-4 border-b border-stroke last:mb-0 last:pb-0 last:border-b-0"
          >
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <SeverityBadge severity={f.severity} />
              <span className="font-sans text-[14px] font-semibold text-content">
                {f.title}
              </span>
            </div>
            {f.description && (
              <div className="font-sans text-[12px] text-content leading-[1.6]">
                {f.description.substring(0, 300)}
                {f.description.length > 300 ? "..." : ""}
              </div>
            )}
          </div>
        ))}
      </Card>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Document-style key/value row used in the executive summary.
// Renders as a flex row on every viewport (label left, value right)
// with a hairline divider above so the rows feel like a structured
// list rather than free-floating stats.
// ──────────────────────────────────────────────────────────────

function SummaryRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex justify-between items-baseline gap-3 py-3 border-t border-stroke first:border-t-0 first:pt-0">
      <dt className="text-muted uppercase tracking-[0.05em] text-[10px]">
        {label}
      </dt>
      <dd className="text-right">{children}</dd>
    </div>
  );
}
